import { useEffect, useState } from "react";
import { BookOpen, Trash2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { StoryPhotoPicker, StoryPhotos } from "@/components/StoryPhotos";
import { imgSrc } from "@/lib/api";

const StoryForm = ({ personId, onAdded }) => {
  const [f, setF] = useState({ title: "", body: "", year: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/persons/${personId}/stories`, { ...f, year: f.year ? parseInt(f.year, 10) : null, photo_paths: photos.map((p) => p.path) });
      setF({ title: "", body: "", year: "" });
      setPhotos([]);
      toast.success(t("Story added"));
      onAdded();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="rounded-3xl bg-[#F4EDE2] p-6 space-y-3" data-testid="story-form">
      <p className="font-serif text-lg text-[#1E3A2B] flex items-center gap-2"><PenLine className="w-4 h-4" />{t("Write a memory")}</p>
      <div className="grid sm:grid-cols-[1fr_140px] gap-3">
        <Input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={t("Title")} className="h-11 rounded-xl bg-white" data-testid="story-title-input" />
        <Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} placeholder={t("Year (optional)")} className="h-11 rounded-xl bg-white" data-testid="story-year-input" />
      </div>
      <Textarea required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} placeholder={t("Share the story…")} className="rounded-xl bg-white min-h-[140px]" data-testid="story-body-input" />
      <StoryPhotoPicker photos={photos} onChange={setPhotos} />
      <Button type="submit" disabled={busy} className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20] px-6" data-testid="story-submit-btn">{t("Add story")}</Button>
    </form>
  );
};

const StoryCard = ({ s, canDelete, onDelete, onOpen }) => {
  const [open, setOpen] = useState(false);
  const long = s.body.length > 420;
  return (
    <article className="rounded-3xl border border-[#E5DEC9] bg-white p-7 space-y-3" data-testid={`story-${s.story_id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl font-semibold">{s.title}</h3>
          <p className="font-mono text-xs text-[#7E8E85] mt-1">{s.year ? `${s.year} · ` : ""}{t("by")} {s.author}</p>
        </div>
        {canDelete && <Button size="icon" variant="ghost" className="text-[#C62828]" onClick={() => onDelete(s)} data-testid={`story-delete-${s.story_id}`}><Trash2 className="w-4 h-4" /></Button>}
      </div>
      <p className="text-[#3A4741] leading-relaxed whitespace-pre-line font-serif text-[17px]">{open || !long ? s.body : `${s.body.slice(0, 420)}…`}</p>
      <StoryPhotos photos={s.photos} onOpen={onOpen} storyId={s.story_id} />
      {long && <button onClick={() => setOpen(!open)} className="text-sm text-[#D96B43] font-medium" data-testid={`story-toggle-${s.story_id}`}>{open ? t("Show less") : t("Read more")}</button>}
    </article>
  );
};

export const PersonStories = ({ personId, name }) => {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [viewing, setViewing] = useState(null);
  const load = () => api.get(`/persons/${personId}/stories`).then(({ data }) => setStories(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [personId]);
  const del = async (s) => {
    if (!window.confirm(`${t("Delete")}: ${s.title}?`)) return;
    try { await api.delete(`/stories/${s.story_id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="space-y-5" data-testid="person-stories">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-2 flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" />{t("Family stories")}</p>
        <h2 className="font-serif text-2xl sm:text-3xl font-semibold">{t("Memories of")} {name}</h2>
      </div>
      {stories.length === 0 && <p className="text-[#7E8E85]" data-testid="stories-empty">{t("No stories yet. Be the first to share a memory.")}</p>}
      {stories.map((s) => <StoryCard key={s.story_id} s={s} canDelete={user?.is_admin || user?.user_id === s.author_id} onDelete={del} onOpen={setViewing} />)}
      {user?.can_vote ? <StoryForm personId={personId} onAdded={load} /> : <p className="text-sm text-[#7E8E85]">{t("Approved family members can add stories.")}</p>}
      {viewing && (
        <Dialog open onOpenChange={() => setViewing(null)}>
          <DialogContent className="sm:max-w-4xl" data-testid="story-photo-lightbox">
            <DialogTitle className="sr-only">Photo</DialogTitle>
            <img src={imgSrc(viewing)} alt="" className="w-full max-h-[75vh] object-contain rounded-xl" />
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};
